import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { SEO } from "@/components/seo/SEO";
import { usePublicCategories } from "@/features/portfolio-categories/hooks";
import { usePublicPortfolioItems } from "@/features/portfolio-items/hooks";
import { PublicError, PublicLoading } from "@/pages/public/PublicState";
import { PortfolioFilters } from "./PortfolioFilters";
import { PortfolioGrid } from "./PortfolioGrid";
import type { PortfolioCategoryOption, PortfolioSort } from "./portfolio-utils";
import { filterPortfolioItems, sortPortfolioItems } from "./portfolio-utils";

const baseCategories: PortfolioCategoryOption[] = [
  { label: "All", value: "all" },
  { label: "Vehicles", value: "vehicles" },
  { label: "Uniforms", value: "uniforms" },
];

function buildCategoryOptions(categories: Array<{ name: string; slug: string }>): PortfolioCategoryOption[] {
  const bySlug = new Map<string, PortfolioCategoryOption>();
  baseCategories.forEach((category) => bySlug.set(category.value, category));
  categories.forEach((category) => bySlug.set(category.slug, { label: category.name, value: category.slug }));
  return Array.from(bySlug.values());
}

export function PortfolioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get("category") || "all";
  const search = searchParams.get("q") || "";
  const sort = (searchParams.get("sort") || "featured") as PortfolioSort;

  const categoriesQuery = usePublicCategories();
  const itemsQuery = usePublicPortfolioItems({});

  const categoryOptions = useMemo(
    () => buildCategoryOptions(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );

  const visibleItems = useMemo(() => {
    const items = itemsQuery.data?.items ?? [];
    return sortPortfolioItems(filterPortfolioItems(items, selectedCategory, search), sort);
  }, [itemsQuery.data?.items, search, selectedCategory, sort]);

  const updateParam = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  if (itemsQuery.isLoading || categoriesQuery.isLoading) return <PublicLoading label="Loading portfolio..." />;
  if (itemsQuery.isError || categoriesQuery.isError) return <PublicError label="Unable to load portfolio content." />;

  return (
    <section className="mx-auto w-full max-w-7xl px-3 py-8 sm:px-6 sm:py-12 lg:px-8">
      <SEO title="Portfolio" description="Browse published Kalaco livery portfolio projects." />
      <CyberPanel>
        <p className="text-sm font-semibold uppercase tracking-[0.28em] text-teal-200">Portfolio</p>
        <div className="mt-3 flex min-w-0 flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h1 className="break-words font-display text-3xl font-bold uppercase text-white sm:text-5xl">Livery Work</h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-content-secondary sm:text-lg sm:leading-8">
              Browse published builds from the CMS by category, client, server, or featured status.
            </p>
          </div>
          <div className="w-full shrink-0 rounded border border-cyan-300/20 bg-black/40 px-4 py-3 text-sm text-content-secondary lg:w-auto">
            <span className="font-bold text-cyan-100">{visibleItems.length}</span> visible / {itemsQuery.data?.total ?? 0} published
          </div>
        </div>
      </CyberPanel>

      <div className="mt-6">
        <PortfolioFilters
          categories={categoryOptions}
          selectedCategory={selectedCategory}
          search={search}
          sort={sort}
          onCategoryChange={(category) => updateParam("category", category, "all")}
          onSearchChange={(value) => updateParam("q", value, "")}
          onSortChange={(value) => updateParam("sort", value, "featured")}
        />
      </div>

      <div className="mt-8">
        <PortfolioGrid items={visibleItems} />
      </div>
    </section>
  );
}
